import React from "react";
import {
  Box,
  Button,
  Collapse,
  Stack,
  Typography,
} from "@mui/material";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import MedicationRoundedIcon from "@mui/icons-material/MedicationRounded";
import QueryStatsRoundedIcon from "@mui/icons-material/QueryStatsRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import colors from "../../../assets/theme/colors";

const quickLogBenefits = [
  { label: "Quick notes", Icon: SendRoundedIcon },
  { label: "Pattern tracking", Icon: QueryStatsRoundedIcon },
  { label: "Caregiver sync", Icon: GroupsRoundedIcon },
];

const fallbackNames = new Set(["", "child", "new profile"]);

const normalizeExampleName = (childName = "") => {
  const name = String(childName || "").trim();
  return fallbackNames.has(name.toLowerCase()) ? "" : name;
};

const getPossessiveName = (name = "") => (
  name.endsWith("s") ? `${name}'` : `${name}'s`
);

const MedicationQuickLogExampleCard = ({ childName = "" }) => {
  const [open, setOpen] = React.useState(false);
  const exampleName = normalizeExampleName(childName);
  const medicationOwnerLabel = exampleName ? getPossessiveName(exampleName) : "the current";
  const exampleChatText = exampleName ? `gave ${exampleName} her morning meds` : "gave morning meds";
  const suggestedChatText = exampleName ? `gave ${exampleName} her meds` : "gave morning meds";

  return (
    <Box
      sx={{
        p: { xs: 1.35, sm: 1.65 },
        borderRadius: 2.5,
        border: "none",
        bgcolor: colors.semantic.profileFlow.medicationManager.guidanceBg,
      }}
    >
      <Stack spacing={0.8}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: "1rem", fontWeight: 900, lineHeight: 1.15, color: colors.landing.heroText }}>
            Log meds faster later
          </Typography>
          <Typography sx={{ mt: 0.55, fontSize: "0.86rem", lineHeight: 1.5, color: colors.semantic.ink2 }}>
            Add {medicationOwnerLabel} medications here carefully once. After that, quick notes like "{suggestedChatText}" can use this list to log doses faster.
          </Typography>
        </Box>

        <Button
          onClick={() => setOpen((current) => !current)}
          aria-expanded={open}
          sx={{ alignSelf: 'flex-start', minWidth: 0, p: 0, color: colors.semantic.profileFlow.medicationManager.actionText, textTransform: 'none', fontSize: '0.82rem', fontWeight: 900 }}
        >
          {open ? 'Hide example' : 'See an example'}&nbsp;
          <Box component="span" aria-hidden="true" sx={{ color: colors.semantic.profileFlow.medicationManager.disclosureIcon }}>{open ? '▴' : '▾'}</Box>
        </Button>

        <Collapse in={open} unmountOnExit>
        <Box
          sx={{
            p: 1,
            borderRadius: 2,
            bgcolor: "#EEF4FB",
            border: "1px dashed #AFC4DC",
          }}
        >
          <Stack direction="row" spacing={0.7} alignItems="center" sx={{ mb: 0.8 }}>
            <Typography
              sx={{
                fontSize: "0.68rem",
                fontWeight: 900,
                color: "#486A91",
                textTransform: "uppercase",
                letterSpacing: "0.1em",
              }}
            >
              Quick note example
            </Typography>
            <Box
              sx={{
                px: 0.65,
                py: 0.2,
                borderRadius: "999px",
                bgcolor: "#FFFFFF",
                border: "1px solid #C8D8EA",
                color: "#486A91",
                fontSize: "0.64rem",
                fontWeight: 900,
                lineHeight: 1.15,
                textTransform: "uppercase",
                letterSpacing: "0.06em",
              }}
            >
              Example only
            </Box>
          </Stack>
          <Stack spacing={0.8}>
            <Box
              sx={{
                alignSelf: "flex-end",
                maxWidth: "85%",
                px: 1.15,
                py: 0.7,
                borderRadius: 1.4,
                bgcolor: "#486A91",
                color: colors.landing.surface,
                fontSize: "0.86rem",
                fontWeight: 700,
                lineHeight: 1.25,
              }}
            >
              {exampleChatText}
            </Box>
            <Stack direction="row" spacing={0.75} alignItems="center">
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: "50%",
                  bgcolor: "#DCE9F6",
                  color: "#486A91",
                  display: "grid",
                  placeItems: "center",
                  flexShrink: 0,
                }}
              >
                <MedicationRoundedIcon sx={{ fontSize: 16 }} />
              </Box>
              <Box
                sx={{
                  minWidth: 0,
                  flex: 1,
                  p: 1,
                  borderRadius: 1.6,
                  bgcolor: colors.semantic.profileFlow.cardBg,
                  border: "1px solid #C8D8EA",
                }}
              >
                <Stack direction="row" spacing={0.35} alignItems="center">
                  <Typography sx={{ fontSize: "0.84rem", fontWeight: 900, color: colors.landing.heroText, lineHeight: 1.2 }}>
                    Logged: Morning meds
                  </Typography>
                  <CheckRoundedIcon sx={{ fontSize: 16, color: "#486A91" }} />
                </Stack>
                <Typography sx={{ mt: 0.3, fontSize: "0.76rem", color: colors.semantic.ink2, lineHeight: 1.35 }}>
                  Risperidone 2 mg - Aripiprazole 5 mg
                </Typography>
                <Typography sx={{ mt: 0.25, fontSize: "0.72rem", color: colors.semantic.ink2, lineHeight: 1.3 }}>
                  Today - 8:03 AM
                </Typography>
              </Box>
            </Stack>
          </Stack>
        </Box>

        <Stack direction="row" spacing={0.55} sx={{ flexWrap: "wrap", rowGap: 0.55 }}>
          {quickLogBenefits.map(({ label, Icon }) => (
            <Box
              key={label}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.35,
                px: 0.7,
                py: 0.35,
                borderRadius: "999px",
                bgcolor: colors.semantic.profileFlow.completionBg,
                color: "#486A91",
                fontSize: "0.72rem",
                fontWeight: 800,
                lineHeight: 1.2,
              }}
            >
              <Icon sx={{ fontSize: 14 }} />
              {label}
            </Box>
          ))}
        </Stack>
        </Collapse>
      </Stack>
    </Box>
  );
};

export default MedicationQuickLogExampleCard;
